import { useMutation, useQuery } from "@tanstack/react-query";
import { Stop, TrainIdentifier } from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { ErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { InternalMessagesForm } from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import { InternalMessageRequest } from "features/CenterContent/RoleContent/InternalMessage/schema/requestSchema";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import { internalMessagesFormToRequest } from "features/CenterContent/RoleContent/InternalMessage/schema/transformers";
import { MessageList } from "features/CenterContent/RoleContent/InternalMessage/shared/MessageList";
import { useFormInternalMessage } from "features/CenterContent/RoleContent/InternalMessage/utils/useForm";
import { INTERNAL_EVENTS_QUERY_KEY } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/EventsOverviewButtonAndModal";
import { BadgeText as BadgeTextStation } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/StationEventModal";
import { BadgeText } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoForm";
import { TrainInternalMessagesQueryKey } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/useTrainInternalMessages";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { ActionModal } from "shared/components/ActionModal";
import { useToastOnError } from "shared/components/dataDisplay/useToastOnError";
import { Flex } from "shared/components/Spor";
import { VisibilityInput } from "shared/form/VisibilityInput";
import { visibilitySchemaResponse } from "shared/form/visibilitySchema";

const INTERNAL_MESSAGES_DETAILS_QUERY_KEY = "internalMessagesDetails";

type InternalMessageType = {
  isGlobal: boolean;
  isTrain: boolean;
  isStation: boolean;
};

const getInternalMessageType = (
  train?: TrainIdentifier,
  stations: Stop[] = [],
  relatedEventUuid: string | undefined = undefined,
): InternalMessageType => ({
  isGlobal: !train && stations.length === 0 && !relatedEventUuid,
  isTrain: !!train,
  isStation: stations.length >= 1,
});

const makeTitle = (
  internalMessageType: InternalMessageType,
  stations: Stop[],
) => {
  const { isGlobal, isTrain, isStation } = internalMessageType;
  if (isGlobal) return `Oppdater global intern melding`;
  if (isTrain) return `Oppdater intern melding til tog`;
  if (isStation)
    return `Oppdater intern melding til ${stations.length >= 2 ? "stasjoner" : "stasjon"}`;
  return `Oppdater intern melding`;
};

const getStations = (event: InternalMessageResponse): Stop[] =>
  event.relevantStops.map((stop) => ({
    name: stop.ids.find((stopId) => stopId.name)?.name!,
    countryCode: event.countryCode,
    stopIds: stop.ids,
  }));

/**
 * Edits every internal messages without related events
 * Functionally similiar to InternalMessage.tsx, but has a visibility field and submit button
 *
 * Documentation: https://vygruppen.atlassian.net/wiki/spaces/TRAFFICGUI/pages/edit-v2/7957217376
 */
const EditInternalMessageDialogContent = ({
  setModalOpen,
  events,
}: {
  setModalOpen: (value: boolean) => void;
  events: InternalMessageResponse[];
}) => {
  // events cannot be empty, this is checked in parent component
  // only look at the latest event
  const event = events[0];
  const train = event.relevantTrain;
  const stations = getStations(event);
  const incidentId = event.referencedIncidentIds?.[0];

  const { relatedEventUuid } = event;
  const internalMessageType = getInternalMessageType(
    train,
    stations,
    relatedEventUuid,
  );
  const { isGlobal, isTrain, isStation } = internalMessageType;
  const formMethods = useFormInternalMessage(
    events,
    visibilitySchemaResponse.parse(event.visibility),
  );
  const { handleSubmit } = formMethods;
  const { mutate: submitRequest, status: submitStatus } = useMutation({
    mutationFn: (data: InternalMessageRequest) =>
      mutationFnPOST<undefined, InternalMessageRequest>(
        `${getBackendUrl()}/global-information/${event.uuid}/change`,
        data,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [INTERNAL_EVENTS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [INTERNAL_MESSAGES_DETAILS_QUERY_KEY, event.uuid],
      });
      void queryClient.invalidateQueries({
        queryKey: TrainInternalMessagesQueryKey(
          train?.nominalDate,
          train?.identifier,
          train?.countryCode,
        ),
      });
    },
  });

  const onSubmit: SubmitHandler<InternalMessagesForm> = ({
    internalMessages,
    visibility,
  }) => {
    if (!visibility) return;
    const request = internalMessagesFormToRequest(
      internalMessages,
      visibility,
      {
        incidentId,
        relevantTrain: train,
        relevantStops: stations,
        relatedEventUuid,
      },
    );
    if (!request) return;
    submitRequest(request);
  };

  return (
    <FormProvider {...formMethods}>
      <ActionModal
        title={makeTitle(internalMessageType, stations)}
        titleBadge={isGlobal ? "Uavhengig" : undefined}
        actionTitle="Send melding"
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit(onSubmit)}
        loading={submitStatus === "pending"}
        isSuccess={submitStatus === "success"}
        isError={submitStatus === "error"}
        successMessage="Melding sendt"
        failureMessage="Kunne ikke sende melding. Prøv på nytt, eller kontakt IT hvis feilen vedvarer"
      >
        <ErrorBoundary>
          <Flex flexDir="column" gap="6px" w="100%">
            {isTrain && <BadgeText selectedTrain={train!} />}
            {isStation && <BadgeTextStation selectedStops={stations} />}
            <MessageList />
            {!isTrain && (
              <VisibilityInput
                visibilityField="visibility"
                isRelatedToIncident={false}
                subField="toTime"
              />
            )}
          </Flex>
        </ErrorBoundary>
      </ActionModal>
    </FormProvider>
  );
};

const EditInternalMessageModal = ({
  setModalOpen,
  uuid,
}: {
  setModalOpen: (value: boolean) => void;
  uuid: string;
}) => {
  const { data, isError } = useQuery({
    queryKey: [INTERNAL_MESSAGES_DETAILS_QUERY_KEY, uuid],
    staleTime: 0,
    queryFn: ({ signal }) =>
      axiosClient
        .get<
          InternalMessageResponse[]
        >(`${getBackendUrl()}/global-information/by-uuid/${uuid}?latest=false`, { signal })
        .then((res) => res.data.reverse()),
  });
  const isEmptyOrError = data?.length === 0 || isError;

  useToastOnError(isEmptyOrError, () => setModalOpen(false));

  if (!data || isEmptyOrError) return null;

  return (
    <EditInternalMessageDialogContent
      events={data}
      setModalOpen={setModalOpen}
    />
  );
};

export default EditInternalMessageModal;
